import InvoiceLangMenu from './InvoiceLangMenu';
import React, { useState } from 'react';
import { Search, Eye, Truck, RotateCcw } from 'lucide-react';
import { Input } from './ui/input';
import OrderDetailDrawer, { ORDER_STATUSES, getStatusColor } from './OrderDetailDrawer';
import { ordersAPI } from '../services/api';
import UnpaidOrdersBanner from './UnpaidOrdersBanner';
import AdminOrderCards from './AdminOrderCards';
import SumupJournalCard from './SumupJournalCard';
import PackShareStatsCard from './PackShareStatsCard';

const AdminOrdersPanel = ({ orders, onStatusChange, onOrderUpdated, onReload }) => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);

  const q = query.trim().toLowerCase();
  const filtered = orders.filter(o =>
    (!status || o.status === status) &&
    (!q || [o.order_number, o.id, o.customer_name, o.shipping_info?.email, o.shipping_info?.city, o.shipping_info?.postalCode]
      .some(v => v && String(v).toLowerCase().includes(q)))
  );

  const countBy = (s) => orders.filter(o => o.status === s).length;

  return (
    <>
      <UnpaidOrdersBanner onChanged={onReload} />
      <SumupJournalCard onOrderChanged={onReload} onOpenOrder={setSelected} />
      <PackShareStatsCard />
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Commandes ({orders.length})</h1>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[12rem]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              data-testid="orders-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="N° commande, client, e-mail, ville…"
              className="pl-9 w-full sm:w-72"
            />
          </div>
          <select data-testid="orders-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="text-sm border rounded-md px-3 py-2 bg-white">
            <option value="">Tous les statuts</option>
            {ORDER_STATUSES.map(s => <option key={s} value={s}>{s} ({countBy(s)})</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
        {ORDER_STATUSES.map(s => (
          <button
            key={s}
            type="button"
            data-testid={`orders-stat-${s}`}
            onClick={() => setStatus(status === s ? '' : s)}
            className={`bg-white rounded-lg shadow-sm p-3 text-left border-2 transition-colors ${status === s ? 'border-orange-500' : 'border-transparent hover:border-gray-200'}`}
          >
            <p className="text-xs text-gray-500">{s}</p>
            <p className="text-xl font-bold text-gray-900">{countBy(s)}</p>
          </button>
        ))}
      </div>

      <div className="md:hidden">
        <AdminOrderCards orders={filtered} onOpen={setSelected} onStatusChange={onStatusChange} />
        {filtered.length === 0 && (
          <p className="text-center py-8 text-gray-500 bg-white rounded-lg shadow-sm" data-testid="orders-empty-mobile">{orders.length === 0 ? 'Aucune commande' : 'Aucune commande ne correspond à votre recherche'}</p>
        )}
      </div>

      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full min-w-[900px]" data-testid="orders-table">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">N°</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Articles</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filtered.map(order => (
              <tr key={order.id} data-testid={`order-row-${order.id}`} onClick={() => setSelected(order.id)} className="hover:bg-orange-50/40 cursor-pointer">
                <td className="px-6 py-4 text-sm font-medium text-blue-600">{order.order_number || `${order.id.slice(0, 8)}...`}</td>
                <td className="px-6 py-4 text-sm text-gray-500 whitespace-nowrap">{new Date(order.created_at).toLocaleDateString('fr-FR')}</td>
                <td className="px-6 py-4 text-sm text-gray-900">
                  {order.customer_name}
                  {order.shipping_info && (
                    <span className="block text-xs text-gray-400">{order.shipping_info.postalCode} {order.shipping_info.city}</span>
                  )}
                </td>
                <td className="px-6 py-4 text-sm text-gray-600">
                  <span className="block">{order.items?.reduce((n, i) => n + i.quantity, 0) || 0} article(s)</span>
                  <span className="block text-xs text-gray-400 truncate max-w-[220px]" title={order.items?.map(i => i.name).join(', ')}>
                    {order.items?.map(i => i.pack_label ? `${i.name} (${i.pack_label})` : i.name).join(', ')}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{order.total.toFixed(2)} €</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>{order.status}</span>
                  {order.payment_status === 'pending' && <span className="ml-1 px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 whitespace-nowrap" data-testid={`order-awaiting-payment-${order.id}`}>Paiement en attente</span>}
                  {order.restocked && (
                    <span className="block text-[11px] text-green-700 mt-1 flex items-center gap-1" data-testid={`order-restocked-${order.id}`}><RotateCcw size={11} /> Stock restitué{order.cancelled_by === 'customer' ? ' (client)' : ''}</span>
                  )}
                  {order.tracking_number && (
                    <span className="block text-[11px] text-gray-400 mt-1 flex items-center gap-1" data-testid={`order-tracking-${order.id}`}><Truck size={11} /> {order.tracking_carrier} {order.tracking_number}</span>
                  )}
                </td>
                <td className="px-6 py-4" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-2">
                    <select
                      data-testid={`order-status-select-${order.id}`}
                      value={order.status}
                      onChange={(e) => onStatusChange(order.id, e.target.value)}
                      className="text-sm border rounded px-2 py-1"
                    >
                      {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <button data-testid={`order-view-${order.id}`} onClick={() => setSelected(order.id)} title="Voir le détail" className="p-2 text-blue-600 hover:bg-blue-50 rounded"><Eye size={16} /></button>
                    <InvoiceLangMenu order={order} variant="icon" label="Télécharger la facture" onDownload={(lang) => ordersAPI.downloadInvoice(order, lang)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <p className="text-center py-8 text-gray-500" data-testid="orders-empty">{orders.length === 0 ? 'Aucune commande' : 'Aucune commande ne correspond à votre recherche'}</p>
        )}
      </div>

      {selected && (
        <OrderDetailDrawer orderId={selected} onClose={() => setSelected(null)} onStatusChange={onStatusChange} onOrderUpdated={onOrderUpdated} />
      )}
    </>
  );
};

export default AdminOrdersPanel;
